import React, { useState } from "react";
import styled from "@emotion/styled";
import { NavLink } from "react-router-dom";
import { Collapse, Chip, ListItemButton, ListItemText } from "@mui/material";
import { ExpandLess, ExpandMore } from "@mui/icons-material";
import { rgba } from "polished";

// Styled link for navigation items
const Link = styled(ListItemButton)`
  padding-left: ${(props) =>
    props.theme.spacing(props.depth && props.depth > 0 ? 14 : 8)};
  padding-top: ${(props) =>
    props.theme.spacing(props.depth && props.depth > 0 ? 2 : 3)};
  padding-bottom: ${(props) =>
    props.theme.spacing(props.depth && props.depth > 0 ? 2 : 3)};
  font-weight: ${(props) => props.theme.typography.fontWeightRegular};
  svg {
    color: ${(props) => props.theme.sidebar.color};
    font-size: 20px;
    width: 20px;
    height: 20px;
    opacity: 0.5;
    margin-right: ${(props) => props.theme.spacing(2)};
  }
  &:hover {
    background: rgba(0, 0, 0, 0.08);
  }
  &.${(props) => props.activeclassname} {
    background-color: ${(props) => rgba(props.theme.sidebar.color, 0.08)};
    span {
      color: ${(props) => props.theme.sidebar.color};
    }
  }
`;

const LinkText = styled(ListItemText)`
  color: ${(props) => props.theme.sidebar.color};
  span {
    font-size: ${(props) => props.theme.typography.body1.fontSize};
  }
  margin-top: 0;
  margin-bottom: 0;
`;

const LinkBadge = styled(Chip)`
  font-size: 11px;
  font-weight: ${(props) => props.theme.typography.fontWeightBold};
  height: 20px;
  position: absolute;
  right: 26px;
  top: 8px;
  background: ${(props) => props.theme.sidebar.badge.background};
  color: ${(props) => props.theme.sidebar.badge.color};
  span.MuiChip-label,
  span.MuiChip-label:hover {
    cursor: pointer;
    color: ${(props) => props.theme.sidebar.badge.color};
    padding-left: ${(props) => props.theme.spacing(2)};
    padding-right: ${(props) => props.theme.spacing(2)};
  }
`;

const CategoryText = styled(ListItemText)`
  margin: 0;
  span {
    color: ${(props) => props.theme.sidebar.color};
    font-size: ${(props) => props.theme.typography.body1.fontSize};
    padding: 0 ${(props) => props.theme.spacing(4)};
  }
`;

const CategoryIconLess = styled(ExpandLess)`
  color: ${(props) => rgba(props.theme.sidebar.color, 0.5)};
`;

const CategoryIconMore = styled(ExpandMore)`
  color: ${(props) => rgba(props.theme.sidebar.color, 0.5)};
`;

const CategoryBadge = styled(LinkBadge)`
  top: 12px;
`;

const Category = styled(ListItemButton)`
  padding-top: ${(props) => props.theme.spacing(3)};
  padding-bottom: ${(props) => props.theme.spacing(3)};
  padding-left: ${(props) => props.theme.spacing(8)};
  padding-right: ${(props) => props.theme.spacing(7)};
  font-weight: ${(props) => props.theme.typography.fontWeightRegular};
  svg {
    color: ${(props) => props.theme.sidebar.color};
    font-size: 20px;
    width: 20px;
    height: 20px;
    opacity: 0.5;
    margin-right: ${(props) => props.theme.spacing(2)};
  }
  &:hover {
    background: rgba(0, 0, 0, 0.08);
  }
  &.${(props) => props.activeclassname} {
    background-color: ${(props) => rgba(props.theme.sidebar.color, 0.08)};
    span {
      color: ${(props) => props.theme.sidebar.color};
    }
  }
`;

const SidebarNavListItem = ({
  title,
  href,
  depth = 0,
  children,
  icon: Icon,
  badge,
  open: openProp = false,
  onClick,
  target,
  rel,
}) => {
  const [open, setOpen] = useState(openProp);

  const handleToggle = () => setOpen((prev) => !prev);

  const handleClick = (e) => {
    if (onClick) {
      onClick(e);
    }
  };

  // If there are children, render collapsible parent (Category)
  if (children) {
    return (
      <React.Fragment>
        <Category onClick={handleToggle}>
          {Icon && <Icon />}
          <CategoryText>{title}</CategoryText>
          {badge && <CategoryBadge label={badge} />}
          {open ? <CategoryIconLess /> : <CategoryIconMore />}
        </Category>
        <Collapse in={open} timeout="auto" unmountOnExit>
          {children}
        </Collapse>
      </React.Fragment>
    );
  }

  // If target="_blank", use a regular anchor tag for external/new tab links
  if (target === "_blank") {
    return (
      <Link
        depth={depth}
        component="a"
        href={href}
        target={target}
        rel={rel || "noopener noreferrer"}
        onClick={handleClick}
      >
        {Icon && <Icon />}
        <LinkText>{title}</LinkText>
        {badge && <LinkBadge label={badge} />}
      </Link>
    );
  }

  // Single clickable item (Link) - internal navigation
  return (
    <Link
      depth={depth}
      component={NavLink}
      to={href}
      activeclassname="active"
      onClick={handleClick}
    >
      {Icon && <Icon />}
      <LinkText>{title}</LinkText>
      {badge && <LinkBadge label={badge} />}
    </Link>
  );
};

export default SidebarNavListItem;
